<template>
  <div class="booking-list-container">
    <!-- Header -->
    <div class="page-header mb-4">
      <el-row :gutter="20" align="middle">
        <el-col :span="12">
          <h2 class="page-title">
            <i class="mdi mdi-calendar-check"></i>
            Booking Management
          </h2>
          <p class="page-subtitle">Manage room reservations and guest bookings</p>
        </el-col>
        <el-col :span="12" class="text-right">
          <el-button type="primary" icon="el-icon-plus" @click="showCreateDialog = true">
            New Booking
          </el-button>
          <el-button icon="el-icon-download" @click="exportBookings">
            Export
          </el-button>
        </el-col>
      </el-row>
    </div>

    <!-- Filters -->
    <el-card class="filter-card mb-4">
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item label="Status">
          <el-select v-model="filters.status" placeholder="All Statuses" clearable @change="fetchBookings">
            <el-option label="All Statuses" value=""></el-option>
            <el-option label="Pending" value="pending"></el-option>
            <el-option label="Confirmed" value="confirmed"></el-option>
            <el-option label="Checked In" value="checked_in"></el-option>
            <el-option label="Checked Out" value="checked_out"></el-option>
            <el-option label="Cancelled" value="cancelled"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="Check-in Date">
          <el-date-picker
            v-model="filters.dateRange"
            type="daterange"
            range-separator="to"
            start-placeholder="Start date"
            end-placeholder="End date"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            @change="fetchBookings"
          />
        </el-form-item>

        <el-form-item label="Search">
          <el-input
            v-model="filters.search"
            placeholder="Guest name, email, or booking code"
            clearable
            @clear="fetchBookings"
            @keyup.enter.native="fetchBookings"
          >
            <el-button slot="append" icon="el-icon-search" @click="fetchBookings"></el-button>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="el-icon-refresh" @click="resetFilters">Reset</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Stats Cards -->
    <el-row :gutter="20" class="mb-4">
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon primary">
              <i class="mdi mdi-calendar-today"></i>
            </div>
            <div class="stat-info">
              <h3>{{ stats.todayCheckIns }}</h3>
              <p>Today's Check-ins</p>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon success">
              <i class="mdi mdi-calendar-check"></i>
            </div>
            <div class="stat-info">
              <h3>{{ stats.activeBookings }}</h3>
              <p>Active Bookings</p>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon warning">
              <i class="mdi mdi-calendar-clock"></i>
            </div>
            <div class="stat-info">
              <h3>{{ stats.pendingBookings }}</h3>
              <p>Pending Confirmation</p>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon info">
              <i class="mdi mdi-percent"></i>
            </div>
            <div class="stat-info">
              <h3>{{ stats.occupancyRate }}%</h3>
              <p>Occupancy Rate</p>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Bookings Table -->
    <el-card>
      <el-table
        :data="bookings"
        v-loading="loading"
        style="width: 100%"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55"></el-table-column>

        <el-table-column prop="booking_code" label="Booking Code" width="120" fixed>
          <template slot-scope="scope">
            <router-link :to="`/bookings/${scope.row.id}`" class="booking-code">
              {{ scope.row.booking_code }}
            </router-link>
          </template>
        </el-table-column>

        <el-table-column prop="guest_name" label="Guest" min-width="180">
          <template slot-scope="scope">
            <div class="guest-info">
              <div class="guest-name">{{ scope.row.guest_name }}</div>
              <div class="guest-contact">
                <i class="el-icon-message"></i> {{ scope.row.guest_email }}
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="room_number" label="Room" width="100">
          <template slot-scope="scope">
            <el-tag size="small">Room {{ scope.row.room_number }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="check_in_date" label="Check-in" width="110">
          <template slot-scope="scope">
            {{ formatDate(scope.row.check_in_date) }}
          </template>
        </el-table-column>

        <el-table-column prop="check_out_date" label="Check-out" width="110">
          <template slot-scope="scope">
            {{ formatDate(scope.row.check_out_date) }}
          </template>
        </el-table-column>

        <el-table-column prop="number_of_guests" label="Guests" width="80" align="center">
          <template slot-scope="scope">
            <i class="mdi mdi-account-multiple"></i> {{ scope.row.number_of_guests }}
          </template>
        </el-table-column>

        <el-table-column prop="total_amount" label="Amount" width="120" align="right">
          <template slot-scope="scope">
            <span class="amount">${{ scope.row.total_amount.toFixed(2) }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="status" label="Status" width="130" align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.status)" effect="dark">
              {{ formatStatus(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Actions" width="200" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button
                v-if="scope.row.status === 'confirmed'"
                type="success"
                size="mini"
                @click="handleCheckIn(scope.row)"
              >
                Check In
              </el-button>
              <el-button
                v-if="scope.row.status === 'checked_in'"
                type="warning"
                size="mini"
                @click="handleCheckOut(scope.row)"
              >
                Check Out
              </el-button>
              <el-dropdown trigger="click" class="ml-2">
                <el-button size="mini" icon="el-icon-more"></el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item @click="viewBooking(scope.row)">
                    <i class="el-icon-view"></i> View Details
                  </el-dropdown-item>
                  <el-dropdown-item @click="editBooking(scope.row)">
                    <i class="el-icon-edit"></i> Edit
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="scope.row.status !== 'cancelled' && scope.row.status !== 'checked_out'"
                    @click="cancelBooking(scope.row)"
                    divided
                  >
                    <i class="el-icon-close"></i> Cancel
                  </el-dropdown-item>
                  <el-dropdown-item @click="printInvoice(scope.row)">
                    <i class="el-icon-printer"></i> Print Invoice
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalItems"
        />
      </div>
    </el-card>

    <!-- Create/Edit Booking Dialog -->
    <create-booking-dialog
      :visible.sync="showCreateDialog"
      :booking="selectedBooking"
      @saved="onBookingSaved"
    />

    <!-- Check-in Dialog -->
    <check-in-dialog
      :visible.sync="showCheckInDialog"
      :booking="selectedBooking"
      @checked-in="onCheckedIn"
    />

    <!-- Check-out Dialog -->
    <check-out-dialog
      :visible.sync="showCheckOutDialog"
      :booking="selectedBooking"
      @checked-out="onCheckedOut"
    />
  </div>
</template>

<script>
import bookingService from '@/services/booking.service'
import CreateBookingDialog from './Modals/CreateBookingDialog.vue'
import CheckInDialog from './Modals/CheckInDialog.vue'
import CheckOutDialog from './Modals/CheckOutDialog.vue'

export default {
  name: 'BookingList',
  components: {
    CreateBookingDialog,
    CheckInDialog,
    CheckOutDialog
  },
  data () {
    return {
      bookings: [],
      loading: false,
      filters: {
        status: '',
        dateRange: null,
        search: ''
      },
      currentPage: 1,
      pageSize: 20,
      totalItems: 0,
      selectedRows: [],
      selectedBooking: null,
      showCreateDialog: false,
      showCheckInDialog: false,
      showCheckOutDialog: false,
      stats: {
        todayCheckIns: 12,
        activeBookings: 45,
        pendingBookings: 8,
        occupancyRate: 67
      }
    }
  },
  mounted () {
    this.fetchBookings()
    this.fetchStats()
  },
  methods: {
    async fetchBookings () {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          per_page: this.pageSize,
          status: this.filters.status,
          search: this.filters.search
        }

        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          params.start_date = this.filters.dateRange[0]
          params.end_date = this.filters.dateRange[1]
        }

        const response = await bookingService.getBookings(params)
        this.bookings = response.data.data
        this.totalItems = response.data.total
      } catch (error) {
        this.$message.error('Failed to load bookings')
      } finally {
        this.loading = false
      }
    },

    async fetchStats () {
      try {
        const response = await this.$bookingService.getBookingStats()
        this.stats = {
          todayCheckIns: response.data.today_check_ins || 12,
          activeBookings: response.data.confirmed_bookings || 45,
          pendingBookings: response.data.pending_bookings || 8,
          occupancyRate: response.data.occupancy_rate || 67
        }
      } catch (error) {
        console.error('Failed to load stats:', error)
      }
    },

    resetFilters () {
      this.filters = {
        status: '',
        dateRange: null,
        search: ''
      }
      this.currentPage = 1
      this.fetchBookings()
    },

    handleSelectionChange (selection) {
      this.selectedRows = selection
    },

    handleSizeChange (val) {
      this.pageSize = val
      this.fetchBookings()
    },

    handleCurrentChange (val) {
      this.currentPage = val
      this.fetchBookings()
    },

    formatDate (date) {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },

    formatStatus (status) {
      return status.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    },

    getStatusType (status) {
      const types = {
        pending: 'warning',
        confirmed: 'primary',
        checked_in: 'success',
        checked_out: 'info',
        cancelled: 'danger'
      }
      return types[status] || 'info'
    },

    viewBooking (booking) {
      this.$router.push(`/bookings/${booking.id}`)
    },

    editBooking (booking) {
      this.selectedBooking = booking
      this.showCreateDialog = true
    },

    async cancelBooking (booking) {
      try {
        await this.$confirm('Are you sure you want to cancel this booking?', 'Cancel Booking', {
          confirmButtonText: 'Yes, Cancel',
          cancelButtonText: 'No',
          type: 'warning'
        })

        await bookingService.cancelBooking(booking.id, 'Cancelled by staff')
        this.$message.success('Booking cancelled successfully')
        this.fetchBookings()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('Failed to cancel booking')
        }
      }
    },

    handleCheckIn (booking) {
      this.selectedBooking = booking
      this.showCheckInDialog = true
    },

    handleCheckOut (booking) {
      this.selectedBooking = booking
      this.showCheckOutDialog = true
    },

    onBookingSaved () {
      this.showCreateDialog = false
      this.selectedBooking = null
      this.fetchBookings()
      this.fetchStats()
    },

    onCheckedIn () {
      this.showCheckInDialog = false
      this.selectedBooking = null
      this.fetchBookings()
      this.fetchStats()
      this.$message.success('Guest checked in successfully')
    },

    onCheckedOut () {
      this.showCheckOutDialog = false
      this.selectedBooking = null
      this.fetchBookings()
      this.fetchStats()
      this.$message.success('Guest checked out successfully')
    },

    async printInvoice (booking) {
      try {
        const response = await bookingService.generateInvoice(booking.id)
        // Here you would implement actual invoice printing/download
        this.$message.success('Invoice generated successfully')
      } catch (error) {
        this.$message.error('Failed to generate invoice')
      }
    },

    exportBookings () {
      // Implement export functionality
      this.$message.info('Export feature coming soon')
    }
  }
}
</script>

<style scoped>
.booking-list-container {
  padding: 20px;
}

.page-header {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.page-title {
  margin: 0;
  font-size: 24px;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title i {
  color: #409EFF;
}

.page-subtitle {
  margin: 5px 0 0;
  color: #909399;
  font-size: 14px;
}

.filter-card {
  margin-bottom: 20px;
}

/* Stat Cards */
.stat-card {
  height: 100%;
  transition: all 0.3s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.stat-content {
  display: flex;
  align-items: center;
  padding: 10px;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  margin-right: 15px;
}

.stat-icon.primary { background: linear-gradient(135deg, #409EFF, #1E88E5); }
.stat-icon.success { background: linear-gradient(135deg, #67C23A, #43A047); }
.stat-icon.warning { background: linear-gradient(135deg, #E6A23C, #F57C00); }
.stat-icon.info { background: linear-gradient(135deg, #909399, #616161); }

.stat-info h3 {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #303133;
}

.stat-info p {
  margin: 5px 0 0;
  color: #909399;
  font-size: 14px;
}

/* Table Styles */
.booking-code {
  color: #409EFF;
  text-decoration: none;
  font-weight: 500;
}

.booking-code:hover {
  text-decoration: underline;
}

.guest-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.guest-name {
  font-weight: 500;
  color: #303133;
}

.guest-contact {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}

.amount {
  font-weight: 600;
  color: #303133;
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.text-right {
  text-align: right;
}

.ml-2 {
  margin-left: 8px;
}

.mb-4 {
  margin-bottom: 16px;
}
</style>
